import * as React from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import CloseIcon from '@mui/icons-material/Close'
import { toast, CloseButtonProps, ToastOptions } from 'react-toastify'
import { useVoiceInput } from '@user/hooks/useVoiceInput'
import { VoiceInputButton } from '@user/components/VoiceInputButton'
import { VOICE_INPUT_BORDER_COLOR } from '@user/features/MapView/constants'
import { useVoiceToTextEnabled } from '@user/store/tenantFeatures/hooks/useVoiceToTextEnabled'

type Props = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  fullWidth?: boolean
  onEnterPress?: () => void
}

export function SearchTextInput({
  value,
  onChange,
  placeholder = 'Search',
  fullWidth = true,
  onEnterPress,
}: Props) {
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const voiceToTextEnabled = useVoiceToTextEnabled()

  React.useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const toastOptions = React.useMemo<ToastOptions>(
    () => ({
      position: 'bottom-center',
      closeButton: (props: CloseButtonProps) => (
        <IconButton
          onClick={props.closeToast}
          data-test-id="mapSearchToastCloseBtn"
          sx={{ color: '#FFFFFF', padding: '4px' }}
        >
          <CloseIcon sx={{ marginLeft: '15px' }} />
        </IconButton>
      ),
      hideProgressBar: true,
      icon: false,
      style: {
        padding: '12px 16px',
        backgroundColor: '#212121',
        color: '#FFFFFF',
        fontSize: '14px',
        minHeight: '40px',
        maxHeight: '40px',
        textAlign: 'center',
      },
    }),
    [],
  )

  const handleMicrophonePermissionDenied = React.useCallback(() => {
    toast.info('Microphone permission is disabled.', toastOptions)
  }, [toastOptions])

  const {
    supported: voiceSupported,
    listening,
    start: startMic,
    stop: stopMic,
    reset: resetMic,
  } = useVoiceInput({
    onResult: (transcript) => {
      onChange(transcript)
    },
    normalize: true,
    onPermissionDenied: handleMicrophonePermissionDenied,
  })
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }
  const handleClear = () => {
    onChange('')
    resetMic()
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onEnterPress?.()
    }
  }

  const handleMicKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      startMic()
    }
  }

  const handleMicKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      stopMic()
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: fullWidth ? '100%' : 'fit-content',
        height: '56px',
        border: '2px solid',
        borderColor: VOICE_INPUT_BORDER_COLOR,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <TextField
        variant="outlined"
        size="medium"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        fullWidth={fullWidth}
        inputRef={inputRef}
        sx={{
          flex: fullWidth ? 1 : undefined,
          '& .MuiOutlinedInput-root': {
            height: '56px',
            '& fieldset': {
              border: 'none',
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment:
            value.length > 0 ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClear}
                  edge="end"
                  size="small"
                  aria-label="clear search"
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '100px',
                    padding: '4px',
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
        }}
      />
      {voiceToTextEnabled && voiceSupported && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: '40px',
            height: '100%',
            borderLeft: '2px solid',
            borderColor: VOICE_INPUT_BORDER_COLOR,
          }}
        >
          <VoiceInputButton
            listening={listening}
            onMouseDown={startMic}
            onMouseUp={stopMic}
            onKeyDown={handleMicKeyDown}
            onKeyUp={handleMicKeyUp}
            ariaLabel="hold to speak"
          />
        </Box>
      )}
    </Box>
  )
}
